import { useInterpret, useSelector } from "@xstate/react";
import { createProtocolInstanceMachine } from "./machine";
import { usePersistedProtocolInstance } from "./usePersistedProtocolInstance";
import { useEffect, useCallback, useRef } from "react";
import { isNotNullNorUndefined } from "@silo/utils";
import {
  ProtocolInstance,
  SupportedChainId,
  getProtocolInstanceChainId,
} from "@silo/types";

interface UseProtocolInstanceParams {
  chainId: number | undefined;
  switchChain: (chainId: SupportedChainId) => Promise<void>;
}

export type SetProtocolInstance = (
  targetProtocolInstance: ProtocolInstance,
  redirectHomeOnError?: boolean
) => void;

export const useProtocolInstance = ({
  chainId,
  switchChain,
}: UseProtocolInstanceParams) => {
  const [persistedProtocolInstance, setPersistedProtocolInstance] =
    usePersistedProtocolInstance();
  /**
   * Making the machine a ref to prevent XState warnings for the machine
   * changing each render. The only dependency of the machine is a local storage
   * value, and we only need to access that value once. The local storage access
   * could actually be done outside of React in the machine itself, but using
   * the hook since it's already set up.
   */
  const machine = useRef(
    createProtocolInstanceMachine(persistedProtocolInstance)
  );

  const actor = useInterpret(machine.current, {
    services: {
      switchChain: (_, event) => {
        const targetChainId = getProtocolInstanceChainId(
          event.targetProtocolInstance
        );

        return switchChain(targetChainId);
      },
    },
  });

  const protocolInstance = useSelector(
    actor,
    (state) => state.context.protocolInstance
  );
  const isAttemptingChainUpdate = useSelector(actor, (state) =>
    state.hasTag("attemptingChainUpdate")
  );
  const isInvalidChain = useSelector(actor, (state) =>
    state.matches("invalidChain")
  );

  const setProtocolInstance = useCallback<SetProtocolInstance>(
    (targetProtocolInstance, redirectOnError = false) => {
      actor.send({
        type: "SET_PROTOCOL_INSTANCE",
        targetProtocolInstance,
        redirectOnError,
      });
    },
    []
  );

  useEffect(() => {
    if (chainId) {
      actor.send({ type: "CHAIN_UPDATED", chainId: chainId });
    }
  }, [chainId]);

  // sync up protocol instance with local storage
  useEffect(() => {
    if (isNotNullNorUndefined(protocolInstance)) {
      setPersistedProtocolInstance(protocolInstance);
    }
  }, [protocolInstance, setPersistedProtocolInstance]);

  return {
    protocolInstance,
    setProtocolInstance,
    isAttemptingChainUpdate,
    isInvalidChain,
  };
};
